import {
  Box,
  Container,
  Heading,
  Image as ChakraImage,
  Stack,
  Text,
} from '@chakra-ui/react'
import NextImage from 'next/image'
import { env } from 'next-runtime-env'
import { useEffect, useMemo, useState } from 'react'
import type {
  IRThemeConfig,
  InvestorRelationsConfigContextType,
} from '@/utils/investorRelations'

interface InvestorRelationsHeroProps {
  useContextFunction: () => InvestorRelationsConfigContextType
  isPreview?: boolean
  organisationId?: string
}

const DEFAULT_HERO_CAROUSEL_SPEED_SECONDS = 5

const getHeroCarouselSpeedMs = (value: unknown): number => {
  const parsedValue =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
        ? Number.parseFloat(value)
        : Number.NaN

  if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
    return DEFAULT_HERO_CAROUSEL_SPEED_SECONDS * 1000
  }

  return parsedValue * 1000
}

function HeroImageLayer({
  alt,
  isActive,
  isPriority,
  shouldShowCarousel,
  src,
}: {
  alt: string
  isActive: boolean
  isPriority: boolean
  shouldShowCarousel: boolean
  src: string
}) {
  const opacity = shouldShowCarousel ? (isActive ? 1 : 0) : 1

  if (src.startsWith('blob:')) {
    return (
      <ChakraImage
        src={src}
        alt={alt}
        position="absolute"
        inset={0}
        w="100%"
        h="100%"
        objectFit="cover"
        objectPosition="center"
        opacity={opacity}
        transition="opacity 800ms ease"
        loading={isPriority ? 'eager' : 'lazy'}
        decoding="async"
      />
    )
  }

  return (
    <Box
      position="absolute"
      inset={0}
      opacity={opacity}
      transition="opacity 800ms ease"
    >
      <NextImage
        src={src}
        alt={alt}
        fill
        sizes="100vw"
        priority={isPriority}
        loading={isPriority ? undefined : 'lazy'}
        unoptimized
        style={{
          objectFit: 'cover',
          objectPosition: 'center',
        }}
      />
    </Box>
  )
}

export function InvestorRelationsHero({
  useContextFunction,
  isPreview = false,
  organisationId = undefined,
}: InvestorRelationsHeroProps) {
  const NEXT_PUBLIC_ASSIDUOUS_API_URL = env('NEXT_PUBLIC_ASSIDUOUS_API_URL')
  const { config, loading, error } =
    useContextFunction() as InvestorRelationsConfigContextType
  const themeConfig = ((config?.themeConfig as IRThemeConfig) ||
    {}) as IRThemeConfig
  const companySlug = config?.companySlugPublished

  const title = themeConfig.title
  const subtitle = themeConfig.subtitle
  const titleAndSubtitleColor = themeConfig.titleAndSubtitleColor
  const fontFamily = themeConfig.fontStyle

  const heroImageSources = useMemo(() => {
    const configuredHeroImageKeys =
      Array.isArray(themeConfig.heroImageUrls) &&
      themeConfig.heroImageUrls.length > 0
        ? themeConfig.heroImageUrls
        : themeConfig.heroImageUrl
          ? [themeConfig.heroImageUrl]
          : []

    const resolveHeroImageSource = (heroImageKey: string): string => {
      if (heroImageKey.startsWith('blob:') || heroImageKey.startsWith('http')) {
        return heroImageKey
      }

      if (isPreview && organisationId) {
        return `/api/proxy/admin/investor-relations/${organisationId}/hero-image-by-key?key=${encodeURIComponent(
          heroImageKey
        )}`
      }

      if (companySlug) {
        return `${NEXT_PUBLIC_ASSIDUOUS_API_URL}/investor-relations/${companySlug}/hero-image-by-key?key=${encodeURIComponent(
          heroImageKey
        )}`
      }

      return ''
    }

    const resolvedHeroImageSources = configuredHeroImageKeys
      .map(resolveHeroImageSource)
      .filter(Boolean)

    if (resolvedHeroImageSources.length > 0) {
      return resolvedHeroImageSources
    }

    if (isPreview && organisationId) {
      return [
        `/api/proxy/admin/investor-relations/${organisationId}/hero-image`,
      ]
    }

    if (companySlug) {
      return [
        `${NEXT_PUBLIC_ASSIDUOUS_API_URL}/investor-relations/${companySlug}/hero-image`,
      ]
    }

    return []
  }, [
    NEXT_PUBLIC_ASSIDUOUS_API_URL,
    companySlug,
    isPreview,
    organisationId,
    themeConfig.heroImageUrl,
    themeConfig.heroImageUrls,
  ])

  const [activeHeroImageIndex, setActiveHeroImageIndex] = useState(0)
  const shouldShowCarousel = heroImageSources.length > 1
  const heroCarouselSpeedMs = getHeroCarouselSpeedMs(
    themeConfig.heroCarouselSpeedSeconds
  )

  const activeHeroImageSourceIndex =
    heroImageSources.length > 0
      ? activeHeroImageIndex % heroImageSources.length
      : 0

  useEffect(() => {
    if (!shouldShowCarousel) return

    const intervalId = window.setInterval(() => {
      setActiveHeroImageIndex((index) => (index + 1) % heroImageSources.length)
    }, heroCarouselSpeedMs)

    return () => window.clearInterval(intervalId)
  }, [heroCarouselSpeedMs, heroImageSources.length, shouldShowCarousel])

  return (
    <Box
      as="section"
      position="relative"
      h={{ base: '280px', md: '420px' }}
      overflow="hidden"
    >
      {heroImageSources.map((heroImageSource, index) => (
        <HeroImageLayer
          key={heroImageSource}
          src={heroImageSource}
          alt=""
          isActive={index === activeHeroImageSourceIndex}
          isPriority={index === 0}
          shouldShowCarousel={shouldShowCarousel}
        />
      ))}

      {/* Dark overlay */}
      <Box
        position="absolute"
        inset={0}
        bgGradient="linear(to-b, blackAlpha.500, blackAlpha.700)"
      />

      <Container
        position="relative"
        maxW="6xl"
        h="100%"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        py={{ base: 4, md: 8 }}
      >
        {/* Hero text */}
        <Stack
          gap={3}
          maxW={{ base: 'lg', md: 'full' }}
          pb={{ base: 4, md: 8 }}
          textAlign="center"
          mx="auto"
        >
          <Heading
            as="h1"
            size={{ base: '5xl', md: '6xl' }}
            color={titleAndSubtitleColor}
            letterSpacing="wide"
            textAlign="center"
            fontWeight="bold"
            fontFamily={fontFamily}
            whiteSpace={{ base: 'normal', md: 'nowrap' }}
            overflowWrap="break-word"
          >
            {title}
          </Heading>
          <Text
            fontSize={{ base: 'xl', md: '2xl' }}
            color={titleAndSubtitleColor}
            fontFamily={fontFamily}
            whiteSpace={{ base: 'normal', md: 'nowrap' }}
            overflowWrap="break-word"
          >
            {subtitle}
          </Text>
        </Stack>
      </Container>

      {shouldShowCarousel && (
        <Stack
          direction="row"
          gap={2}
          position="absolute"
          bottom={{ base: 4, md: 6 }}
          left="50%"
          transform="translateX(-50%)"
          zIndex={2}
        >
          {heroImageSources.map((heroImageSource, index) => (
            <Box
              key={heroImageSource}
              as="button"
              aria-label={`Show hero image ${index + 1}`}
              onClick={() => setActiveHeroImageIndex(index)}
              w={{ base: '8px', md: '10px' }}
              h={{ base: '8px', md: '10px' }}
              borderRadius="full"
              bg={
                index === activeHeroImageSourceIndex
                  ? 'white'
                  : 'whiteAlpha.600'
              }
              border="1px solid"
              borderColor="whiteAlpha.800"
              cursor="pointer"
            />
          ))}
        </Stack>
      )}
    </Box>
  )
}
